import { List, Map } from "immutable";

import {
	type ColumnAppendAction,
	type ColumnDeleteAction,
	type ColumnRenameAction,
	type MutationAction,
	type QueryRegistrationAction,
} from "./Action";
import { type QueryEvaluatorState } from "./QueryManagerState";
import { type TableMutation } from "./TableMutation";

export type MutationActionIntent = {
	action: MutationAction;
	proposedState: QueryEvaluatorState;
	changesByQueryId: Map<string, List<TableMutation<unknown>>>;
};
export const isMutationActionIntent = (
	actionIntent: unknown
): actionIntent is MutationActionIntent => {
	return typeof actionIntent === "object" && actionIntent?.["action"]?.["type"] === "mutation";
};

export type RegisterQueryActionIntent = {
	action: QueryRegistrationAction;
	proposedState: QueryEvaluatorState;
};

export interface ColumnAppendActionIntent {
	action: ColumnAppendAction;
	proposedState: QueryEvaluatorState;
	changesByQueryId: Map<string, List<TableMutation<unknown>>>;
}

export const isColumnAppendActionIntent = (
	actionIntent: unknown
): actionIntent is ColumnAppendActionIntent => {
	return (
		typeof actionIntent === "object" && actionIntent?.["action"]?.["type"] === "columnAppend"
	);
};

export interface ColumnDeleteActionIntent {
	action: ColumnDeleteAction;
	proposedState: QueryEvaluatorState;
	changesByQueryId: Map<string, List<TableMutation<unknown>>>;
}

export const isColumnDeleteActionIntent = (
	actionIntent: unknown
): actionIntent is ColumnDeleteActionIntent => {
	return (
		typeof actionIntent === "object" && actionIntent?.["action"]?.["type"] === "columnDelete"
	);
};

export interface ColumnRenameActionIntent {
	action: ColumnRenameAction;
	proposedState: QueryEvaluatorState;
	changesByQueryId: Map<string, List<TableMutation<unknown>>>;
}

export const isColumnRenameActionIntent = (
	actionIntent: unknown
): actionIntent is ColumnRenameActionIntent => {
	return (
		typeof actionIntent === "object" && actionIntent?.["action"]?.["type"] === "columnRename"
	);
};

export type ActionIntent =
	| MutationActionIntent
	| RegisterQueryActionIntent
	| ColumnAppendActionIntent
	| ColumnDeleteActionIntent
	| ColumnRenameActionIntent;

export interface ReconciliationResult {
	rollbackActions: List<MutationActionIntent>;

	// Actions that have not been acknowledged yet.
	remainingPendingActions: List<ActionIntent>;

	// The new state after applying the reconciliation actions.
	newCommittedState: QueryEvaluatorState;
}

const reconciliationResult = (
	newCommittedState: QueryEvaluatorState,
	remainingPendingActions: List<ActionIntent>,
	rollbackActions: List<MutationActionIntent>
): ReconciliationResult => ({
	remainingPendingActions,
	newCommittedState,
	rollbackActions,
});

export const reconcileQueryEvaluatorState = (
	state: QueryEvaluatorState,
	pendingActions: List<ActionIntent>
): ReconciliationResult => {
	// The goal of this function is to take a list of pending `ActionIntent` and compute a set of
	// `ReconciliationActions` that need to be performed to bring the current state up to date with
	// the pending actions. Primarily this means a couple of things:
	//
	// 1. Check the `state.currentEpoch` against the `desiredApplyEpoch` of the first pending
	//    action.
	// 2. If the two epochs disagree, we need to get the state's `actionLog` slice starting from the
	//    `desiredApplyEpoch` and check for conflicts. To check for conflicts, we need to check if
	//    any pending mutations change a record that was changed in the `actionLog` slice.
	// 3. If there are conflicts, we need to roll back the state to the state at the
	//    `desiredApplyEpoch` and apply the pending actions again. We can do this by inverting the
	//    JSON patch in the mutation. Note that these patches need to be returned by the
	//    `ReconciliationActions` in reverse order! Otherwise they will not cleanly apply.
	// 4. If there are no conflicts, we can simply apply the actions from the `actionLog` slice in
	//    the order that they appear.

	//
	// Get the epoch of the first pending action.
	//

	const firstPendingAction = pendingActions.first();
	if (!firstPendingAction) {
		return reconciliationResult(state, pendingActions, List());
	}

	// Get the epoch based on action type
	const firstPendingActionEpoch = (() => {
		const action = firstPendingAction.action;
		switch (action.type) {
			case "queryRegistration":
				return action.desiredStartEpoch;
			case "mutation":
			case "columnAppend":
			case "columnDelete":
			case "columnRename":
				return action.desiredApplyEpoch;
			default: {
				action satisfies never;
				throw new Error(`Unknown action type: ${firstPendingAction.action.type}`);
			}
		}
	})();

	//
	// Get all actions confirmed by the authority since the first pending action's epoch.
	//

	const [, confirmedActionsSinceStartEpoch] = state.actionLog.partition((action) => {
		const type = action.type;
		switch (action.type) {
			case "queryRegistration":
				return action.desiredStartEpoch <= firstPendingActionEpoch;
			case "mutation":
			case "columnAppend":
			case "columnDelete":
			case "columnRename":
				return action.desiredApplyEpoch <= firstPendingActionEpoch;
			default:
				action satisfies never;
				throw new Error(`Unknown action type: ${type}`);
		}
	});

	if (confirmedActionsSinceStartEpoch.size === 0) {
		return reconciliationResult(state, pendingActions, List());
	}

	//
	// Find the first conflict between the confirmed actions and the pending actions.
	//

	const length = Math.max(confirmedActionsSinceStartEpoch.size, pendingActions.size);
	let firstConflictIndex: number | undefined;
	let i = length;
	for (; i < length; i++) {
		const confirmedAction = confirmedActionsSinceStartEpoch.get(i);
		const pendingAction = pendingActions.get(i);

		if (confirmedAction?.intentId !== pendingAction?.action.intentId) {
			firstConflictIndex = i;
			break;
		}
	}

	//
	// If there is no conflict, return the last acknowledged state.
	//

	if (firstConflictIndex === undefined) {
		return reconciliationResult(pendingActions.last()!.proposedState, List(), List());
	}

	//
	// Find all conflicting actions.
	//

	const [, conflictingActions] = pendingActions.partition(
		(_action, index) => index < firstConflictIndex
	);

	const mutationsByTableAndRecordId: Map<string, Map<string, List<MutationActionIntent>>> = Map();
	for (const action of conflictingActions) {
		if (!isMutationActionIntent(action)) {
			continue;
		}

		const mutationsByRecord = mutationsByTableAndRecordId.get(
			action.action.table,
			Map<string, List<MutationActionIntent>>()
		);
		const mutationsOfRecord = mutationsByRecord.get(
			action.action.mutation.recordId,
			List<MutationActionIntent>()
		);

		mutationsByTableAndRecordId.set(
			action.action.table,
			mutationsByRecord.set(action.action.mutation.recordId, mutationsOfRecord.push(action))
		);
	}

	let newPendingActions = List<ActionIntent>();
	let rollbackActions: Map<string, Map<string, List<MutationActionIntent>>> = Map();
	for (const record of pendingActions.slice(firstConflictIndex)) {
		if (!isMutationActionIntent(record)) {
			newPendingActions = newPendingActions.push(record);
			continue;
		}

		const needsToRollback =
			mutationsByTableAndRecordId
				.get(record.action.table)
				?.get(record.action.mutation.recordId) !== undefined;

		if (!needsToRollback) {
			newPendingActions = newPendingActions.push(record);
			continue;
		}

		const mutationsByRecord = mutationsByTableAndRecordId.get(
			record.action.table,
			Map<string, List<MutationActionIntent>>()
		);
		const mutationsOfRecord = mutationsByRecord.get(
			record.action.mutation.recordId,
			List<MutationActionIntent>()
		);

		rollbackActions = rollbackActions.set(
			record.action.table,
			mutationsByRecord.set(record.action.mutation.recordId, mutationsOfRecord.push(record))
		);
	}

	const rollbacks = rollbackActions
		.toList()
		.flatMap((conflictsForTable) => conflictsForTable.values())
		.flatMap((conflictsForRecord) => conflictsForRecord);
	return reconciliationResult(state, pendingActions, rollbacks);
};
